import { apiClient } from '@/shared/services/api';
import type { ApiResponse } from '@/features/auth/services/authApi';
import { randomId } from '@/shared/utils/randomId';

export type MovementType = 'Receipt' | 'Issue' | 'Return' | 'Adjustment';

export interface StockMovement {
  id: string;
  inputId: string;
  inputBatchId: string;
  movementType: MovementType;
  quantity: number;
  movementDate: string;
  referenceNumber: string;
  greenhouseId?: string;
  farmId?: string;
  fieldId?: string;
  productionBatchNumber: string;
  reason: string;
  notes: string;
  requiresApproval: boolean;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

export interface CreateStockMovementRequest {
  inputId: string;
  inputBatchId: string;
  movementType: MovementType;
  quantity: number;
  movementDate: string;
  referenceNumber: string;
  greenhouseId?: string;
  farmId?: string;
  fieldId?: string;
  productionBatchNumber: string;
  reason: string;
  notes: string;
  requiresApproval: boolean;
}

export interface ApprovalRequest {
  id: string;
  isApproved: boolean;
  notes: string;
}

export interface IssueStockRequest {
  idempotencyKey?: string;
  inputId: string | null;
  quantity: number;
  movementDate: string;
  requisitionId: string;
  requisitionItemId: string;
  productionBatchNumber?: string;
  greenhouseId?: string;
  farmId?: string;
  fieldId?: string;
  notes?: string;
  unit: number;
}

export const stockMovementsApi = {
  recordReceipt: async (payload: CreateStockMovementRequest): Promise<StockMovement> => {
    const { data } = await apiClient.post<ApiResponse<StockMovement>>(
      '/Stock/receipts',
      payload
    );
    return data.data;
  },

  recordIssue: async (payload: CreateStockMovementRequest): Promise<StockMovement> => {
    const { data } = await apiClient.post<ApiResponse<StockMovement>>(
      '/Stock/issues',
      payload,
      { headers: { 'Idempotency-Key': randomId() } }
    );
    return data.data;
  },

  issueStockForRequisition: async ({ idempotencyKey, ...payload }: IssueStockRequest): Promise<StockMovement> => {
    const { data } = await apiClient.post<ApiResponse<StockMovement>>(
      '/Stock/issues',
      payload,
      { headers: { 'Idempotency-Key': idempotencyKey ?? randomId() } }
    );
    return data.data;
  },

  recordReturn: async (payload: CreateStockMovementRequest): Promise<StockMovement> => {
    const { data } = await apiClient.post<ApiResponse<StockMovement>>(
      '/Stock/returns',
      payload
    );
    return data.data;
  },

  recordAdjustment: async (payload: CreateStockMovementRequest): Promise<StockMovement> => {
    const { data } = await apiClient.post<ApiResponse<StockMovement>>(
      '/Stock/adjustments',
      payload
    );
    return data.data;
  },

  getMovementsByInput: async (
    inputId: string,
    startDate?: string,
    endDate?: string
  ): Promise<StockMovement[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const { data } = await apiClient.get<ApiResponse<StockMovement[]>>(
      `/Stock/movements/${inputId}?${params.toString()}`
    );
    return data.data;
  },

  getPendingApprovals: async (): Promise<StockMovement[]> => {
    const { data } = await apiClient.get<ApiResponse<StockMovement[]>>(
      '/Stock/pending-approvals'
    );
    return data.data;
  },

  approveMovement: async (payload: ApprovalRequest): Promise<void> => {
    await apiClient.post('/Stock/approve', payload);
  },
};
